/**
 * Nommer les endroits : adresses, et surtout communes.
 *
 * Nommer un feu demande la COMMUNE, pas l'adresse : les incendies sont en
 * forêt, où la Base Adresse Nationale ne trouve rien. geo.api.gouv.fr renvoie
 * la commune qui contient le point, partout en France.
 */
import type { GeocodeResult, Place } from '../core/types';
import { zoneId } from '../core/zones';
import { getJson } from './http';

const BAN = 'https://api-adresse.data.gouv.fr';
const GEO_API = 'https://geo.api.gouv.fr';

interface BanFeature {
  properties?: { label?: string; city?: string; context?: string; postcode?: string };
  geometry?: { coordinates?: [number, number] };
}
interface BanResponse {
  features?: BanFeature[];
}

/** Propositions de la barre de recherche. */
export async function geocode(query: string): Promise<GeocodeResult[]> {
  const data = await getJson<BanResponse>(
    `${BAN}/search/?limit=5&q=${encodeURIComponent(query)}`,
  );
  const features = data.features ?? [];

  return features.flatMap((feature): GeocodeResult[] => {
    const coordinates = feature.geometry?.coordinates;
    const label = feature.properties?.label;
    if (!coordinates || !label) return [];
    return [
      {
        label,
        context: feature.properties?.context ?? '',
        lat: coordinates[1],
        lon: coordinates[0],
      },
    ];
  });
}

/** Adresse la plus proche d'un point. Sert à nommer la position de l'utilisateur. */
export async function reverseGeocode(lat: number, lon: number): Promise<Place | null> {
  try {
    const data = await getJson<BanResponse>(`${BAN}/reverse/?lat=${lat}&lon=${lon}`);
    const properties = data.features?.[0]?.properties;
    if (!properties) return null;
    const label = properties.city ?? properties.label;
    if (!label) return null;
    return { label, department: (properties.postcode ?? '').slice(0, 2) || null };
  } catch {
    return null;
  }
}

interface CommuneRecord {
  nom?: string;
  codeDepartement?: string;
}

/** La commune qui contient un point, partout en France. */
async function fetchCommune(lat: number, lon: number): Promise<Place | null> {
  try {
    const data = await getJson<CommuneRecord[]>(
      `${GEO_API}/communes?lat=${lat}&lon=${lon}&fields=nom,codeDepartement`,
    );
    const first = Array.isArray(data) ? data[0] : undefined;
    if (!first?.nom) return null;
    return { label: first.nom, department: first.codeDepartement ?? null };
  } catch {
    return null;
  }
}

/**
 * Cache de noms de lieux.
 *
 * Sans lui, chaque balayage reperdrait les noms déjà résolus et relancerait
 * une dizaine de requêtes pour un résultat identique. La clé est celle des
 * zones de feu, ce qui permet de réhydrater le cache depuis le dernier
 * balayage mémorisé.
 */
export interface PlaceLookup {
  peek(lat: number, lon: number): Place | null;
  remember(lat: number, lon: number, place: Place): void;
  /** Résout, avec repli sur l'adresse si la commune est introuvable. */
  resolve(lat: number, lon: number): Promise<Place | null>;
  snapshot(): ReadonlyMap<string, Place>;
}

export function createPlaceLookup(): PlaceLookup {
  const cache = new Map<string, Place>();

  return {
    peek(lat, lon) {
      return cache.get(zoneId(lat, lon)) ?? null;
    },
    remember(lat, lon, place) {
      cache.set(zoneId(lat, lon), place);
    },
    async resolve(lat, lon) {
      const key = zoneId(lat, lon);
      const cached = cache.get(key);
      if (cached) return cached;

      // Hors de France, ou service momentanément indisponible.
      const place = (await fetchCommune(lat, lon)) ?? (await reverseGeocode(lat, lon));
      if (place) cache.set(key, place);
      return place;
    },
    snapshot() {
      return cache;
    },
  };
}
